import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader, Menu, X } from 'lucide-react';
import axios from 'axios';
import "../styles.css";
import Footer from './footer';

const VinoPage = () => {
  const [htmlContent, setHtmlContent] = useState('');
  const [activeSection, setActiveSection] = useState('');
  const activeSectionRef = useRef(activeSection);
  const [isTocOpen, setIsTocOpen] = useState(false);
  const manualSelectionTimeoutRef = useRef(null);

  const sections = [
    { id: "1.-Domain-specific-area-and-objectives-of-the-project", title: "Introduction" },
    { id: "2.-Dataset-Description", title: "Dataset Description" },
    { id: "3.-Data-Preparation", title: "Data Preparation" },
    { id: "4.-Statistical-Analysis", title: "Statistical Analysis" },
    { id: "5.-Visualisation", title: "Visualisation" },
    { id: "6.-Building-The-Machine-Learning-Model", title: "Building The ML Model" },
    { id: "7.-Validation", title: "Validation" },
    { id: "8.-Feature-Engineering", title: "Feature Engineering" },
    { id: "9.-Evaluation-Of-The-Model", title: "Conclusion" },
  ];

  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);

  useEffect(() => {
    const fetchHtmlContent = async () => {
      try {
        const response = await axios.get(`${process.env.PUBLIC_URL}/vino.html`);
        setHtmlContent(response.data);
      } catch (error) {
        console.error('Failed to load HTML content', error);
      }
    };

    fetchHtmlContent();

    const style = document.createElement('style');
    style.innerHTML = `
      [id].scroll-section {
        scroll-margin-top: 90px;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      .loading-spinner {
        animation: spin 1.5s linear infinite;
      }

      .toc-mobile {
        position: fixed;
        top: 0;
        right: 0;
        height: 100%;
        width: 80%;
        max-width: 300px;
        background-color: #0f0f0f;
        z-index: 50;
        padding: 1rem;
        transform: translateX(100%);
        transition: transform 0.3s ease-in-out;
        box-shadow: -2px 0 10px rgba(0, 0, 0, 0.5);
        border-left: 1px solid rgba(255, 255, 255, 0.05);
      }

      .toc-mobile.open {
        transform: translateX(0);
      }

      .toc-mobile-backdrop {
        content: '';
        position: fixed;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.3);
        z-index: 40;
      }

      .toc-item {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
        font-size: 14px;
        padding: 2px 0;
        transition: color 0.2s ease, opacity 0.2s ease;
        position: relative;
      }
      
      .toc-item.active {
        color: white;  
        opacity: 1;
        font-size: 16px;
      }
      
      .toc-item.inactive {
        color: rgba(204, 210, 218, 0.5);
        opacity: 0.5;
      }
      
      .toc-item.active::before {
        content: '';
        position: absolute;
        left: -10px;
        top: 50%;
        transform: translateY(-50%);
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background-color: white;
      }

      @media (min-width: 1280px) {
        .toc-toggle-btn {
          display: none;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  useEffect(() => {
    if (!htmlContent) return;

    const handleScroll = () => {
      if (manualSelectionTimeoutRef.current) return;

      let newActiveSection = '';
      const topThreshold = 150;

      sections.forEach((section) => {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top < topThreshold && rect.top > -50) {
            newActiveSection = section.id;
          }
        }
      });

      if (!newActiveSection) {
        let lastSectionAboveViewport = '';

        sections.forEach((section) => {
          const element = document.getElementById(section.id);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 0) {
              lastSectionAboveViewport = section.id;
            }
          }
        });

        if (lastSectionAboveViewport) {
          newActiveSection = lastSectionAboveViewport;
        }
      }

      if (newActiveSection && newActiveSection !== activeSectionRef.current) {
        setActiveSection(newActiveSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    setTimeout(handleScroll, 100);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [htmlContent]);

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();

    setActiveSection(sectionId);

    if (manualSelectionTimeoutRef.current) {
      clearTimeout(manualSelectionTimeoutRef.current);
    }

    manualSelectionTimeoutRef.current = setTimeout(() => {
      manualSelectionTimeoutRef.current = null;
    }, 1000);

    const element = document.getElementById(sectionId);

    if (element) {
      const offsetPosition = element.offsetTop - 100;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      setIsTocOpen(false);
    }
  };

  const toggleToc = () => {
    setIsTocOpen(!isTocOpen);
  };

  return (
    <div className="flex flex-col min-h-screen bg-black text-gray-200">
      {/* Header */}
      <header className="px-6 py-4 lg:px-12 fixed top-0 left-0 right-0 z-20 bg-black">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors bg-gray-600/30 rounded-full px-4 py-2.5">
            <ArrowLeft size={16} />
            <span className="font-plex text-sm">Back</span>
          </Link>
          <div className="text-center">
            <div className="font-space text-xl text-white font-bold">A Wine Quality Analysis</div>
            <p className="font-plex text-sm gradient-text fade-in-text mt-1">The Secret to Crafting Top-Quality Wine</p>
          </div>
          <div className="w-10"></div>
        </div>
      </header>

      {/* Main Section */}
      <div className="flex flex-col xl:flex-row justify-center max-w-8xl mx-auto pt-28 px-6 sm:px-12">
        <div className="hidden lg:block w-1/6"></div>

        {/* Main HTML Content */}
        <main className="w-full xl:w-3/6">
          <div className="bg-black rounded-xl p-6 shadow-lg">
            {htmlContent ? (
              <div
                dangerouslySetInnerHTML={{ __html: htmlContent }}
                className="prose prose-invert max-w-none"
              />
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <Loader className="text-blue-500 loading-spinner mb-4" size={36} />
                <p className="text-gray-400 font-plex text-sm">Loading content...</p>
              </div>
            )}
          </div>
        </main>

        {/* Right TOC */}
        {htmlContent && (
          <aside className="hidden xl:block sticky top-32 self-start ml-24 w-48">
            <h3 className="font-plex font-medium uppercase tracking-wider text-gray-500 mb-6">
              C0NTENTS
            </h3>
            <nav className="w-full">
              <ul className="space-y-4">
                {sections.map(section => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      onClick={(e) => scrollToSection(e, section.id)}
                      className={`font-plex block toc-item ${activeSection === section.id ? 'active' : 'inactive'
                        }`}
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>
        )}
      </div>

      {/* Mobile TOC Button */}
      {htmlContent && (
        <button
          onClick={toggleToc}
          className="toc-toggle-btn flex items-center justify-center text-gray-400 hover:text-white transition-colors bg-gray-600/30 rounded-full w-10 h-10 fixed bottom-6 right-6 z-50"
          aria-label="Toggle table of contents"
        >
          {isTocOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      )}

      {/* Mobile TOC Backdrop */}
      {htmlContent && isTocOpen && (
        <div
          className="toc-mobile-backdrop xl:hidden"
          onClick={() => setIsTocOpen(false)}
        />
      )}

      {/* Mobile TOC Overlay */}
      {htmlContent && (
        <div className={`toc-mobile ${isTocOpen ? 'open' : ''} xl:hidden`}>
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-plex text-sm font-medium uppercase tracking-wider text-gray-500 mb-2">
              C0NTENTS
            </h3>
            <button
              onClick={toggleToc}
              className="text-gray-400 hover:text-white transition-colors"
              aria-label="Close table of contents"
            >
              <X size={18} />
            </button>
          </div>
          <nav className="w-full">
            <ul className="space-y-4">
              {sections.map(section => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    onClick={(e) => scrollToSection(e, section.id)}
                    className={`font-plex block toc-item ${activeSection === section.id ? 'active' : 'inactive'
                      }`}
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default VinoPage;